/*
 * This file is part of glowingblue/orell-facade.
 *
 * Copyright (c) 2023 Glowing Blue AG.
 * Authors: Rafael Horvat.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import { components, utils } from '@glowingblue-orell-thalia-common';
import Banner from '../models/Banner';
import SliderItem from './SliderItem';
import BannerSkeleton from './BannerSkeleton';
import app from 'flarum/forum/app';

const pauseIcon = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="1.8 1.8 16.4 16.4"><rect width="5" height="15" x="2.625" y="2.5" stroke="currentColor" stroke-width="1.2" rx="1"/><rect width="5" height="15" x="12.375" y="2.5" stroke="currentColor" stroke-width="1.2" rx="1"/></svg>`;
const playIcon = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="1.8 1.8 16.4 16.4"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.2" d="M3.297 16.598V3.334a1 1 0 0 1 1.494-.87L16.85 9.326a1 1 0 0 1-.025 1.753L4.766 17.48a1 1 0 0 1-1.47-.883"/></svg>`;

export default class HomeSlider extends components.AbstractSlider {
	oninit(vnode) {
		super.oninit(vnode);
		this.loading = true;
		this.autoplayPaused = false;
		this.refresh();
	}

	// Not `swiperOptionOverrides()`: that hook is absent in older common builds, where overriding it
	// would silently drop everything below.
	getSwiperOptions() {
		const options = super.getSwiperOptions();

		return {
			...options,
			slidesPerView: 1,
			centeredSlides: false,
			// The base switches both off for a single slide, so adjust rather than reinstate.
			autoplay: options.autoplay && { ...options.autoplay, delay: 8000 },
			pagination: options.pagination && { ...options.pagination, dynamicBullets: false },
		};
	}

	get sliderItemComponent() {
		return SliderItem;
	}

	initOrUpdateSwiper() {
		super.initOrUpdateSwiper();

		// The base re-runs this on every redraw and restarts autoplay, which would undo the pause the
		// toggle just triggered a redraw for.
		if (this.autoplayPaused) {
			this.swiper?.autoplay?.stop();
		}
	}

	view() {
		const { items } = this;

		if (!items.length) {
			return <div className="HomeSlider">{this.loading && <BannerSkeleton />}</div>;
		}

		const tree = super.view();

		const navigation = utils.vdom.findChild(tree, 'Slider-body--navigation', true);
		if (navigation && Array.isArray(navigation.children)) {
			navigation.children.push(this.autoplayToggle());
		}

		return tree;
	}

	autoplayToggle() {
		const paused = this.autoplayPaused;
		const label = app.translator.trans(`glowingblue-orell-facade.forum.slider.${paused ? 'resume' : 'pause'}`);

		return (
			<button type="button" className="Slider-autoplayToggle" aria-label={label} title={label} onclick={() => this.toggleAutoplay()}>
				{m.trust(paused ? playIcon : pauseIcon)}
			</button>
		);
	}

	toggleAutoplay() {
		const autoplay = this.swiper?.autoplay;
		if (!autoplay) return;

		if (this.autoplayPaused) {
			autoplay.start();
		} else {
			autoplay.stop();
		}

		this.autoplayPaused = !this.autoplayPaused;
	}

	async refresh(clear = true, position = 'slider') {
		if (clear) {
			this.clear();
		}

		try {
			const banners = await app.store.find('banners', {
				sort: '-priority',
				filter: { position: position, active: true, random: true },
			});

			this.parseResults(banners);
		} catch (e) {
			// Just make sure the loading indicator is hidden
		}

		this.loading = false;
		m.redraw();
	}

	clear() {
		this.loading = true;
		this.items = [];
		m.redraw();
	}

	/**
	 * @param {Banner[]} results
	 * @return {Banner[]}
	 */
	parseResults(results) {
		const priorityItems = results
			.filter((item) => item.setup().priority > 0)
			.sort((a, b) => {
				if (a.setup().priority > b.setup().priority) return -1;
				if (a.setup().priority < b.setup().priority) return 1;
				return 0;
			});
		const normalItems = results.filter((item) => item.setup().priority <= 0);

		this.items = [...priorityItems, ...normalItems];

		this.loading = false;
		m.redraw();
	}
}
